import * as React from 'react'
import { useEventCallback, deepClone } from '@applift/factor'
import {
  useGridApiEventHandler,
  useGridApiOptionHandler,
} from '../../utils/useGridApiEventHandler'
import { GridEventListener } from '../../../models/events/gridEventListener'
import {
  GridEditModes,
  GridRowModes,
  GridEditingState,
  GridEditCellProps,
  GridEditRowProps,
} from '../../../models/gridEditRowModel'
import { GridPrivateApiCommunity } from '../../../models/api/gridApiCommunity'
import { DataGridProcessedProps } from '../../../models/props/DataGridProps'
import {
  GridRowEditingApi,
  GridEditingSharedApi,
  GridStopRowEditModeParams,
  GridStartRowEditModeParams,
  GridRowModesModel,
  GridRowModesModelProps,
  GridRowEditingPrivateApi,
  GridEditingSharedPrivateApi,
} from '../../../models/api/gridEditingApi'
import { useGridApiMethod } from '../../utils/useGridApiMethod'
import { gridEditRowsStateSelector } from './gridEditingSelectors'
import { ReactTableRowId } from '../../../models/gridReactTable'
import { isPrintableKey } from '../../../utils/keyboardUtils'
import { GridCellParams } from '../../../models/params/gridCellParams'
import {
  GridRowEditStopParams,
  GridRowEditStartParams,
  GridRowEditStopReasons,
  GridRowEditStartReasons,
} from '../../../models/params/gridRowParams'

const missingOnProcessRowUpdateErrorWarning = () => {
  console.warn(
    [
      'A call to `processRowUpdate` threw an error which was not handled because `onProcessRowUpdateError` is missing.',
      'To handle the error pass a callback to the `onProcessRowUpdateError` prop, e.g. `<DataGrid onProcessRowUpdateError={(error) => ...} />`.',
    ].join()
  )
}

export const useGridRowEditing = (
  apiRef: React.MutableRefObject<GridPrivateApiCommunity>,
  props: Pick<
    DataGridProcessedProps,
    | 'editMode'
    | 'processRowUpdate'
    | 'onRowEditStart'
    | 'onRowEditStop'
    | 'onProcessRowUpdateError'
    | 'rowModesModel'
    | 'onRowModesModelChange'
  >
) => {
  const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>(
    {}
  )
  const rowModesModelRef = React.useRef(rowModesModel)
  const prevRowModesModel = React.useRef<GridRowModesModel>({})
  const focusTimeout = React.useRef<any>(null)
  const nextFocusedCell = React.useRef<GridCellParams | null>(null)

  const {
    processRowUpdate,
    onProcessRowUpdateError,
    rowModesModel: rowModesModelProp,
    onRowModesModelChange,
  } = props

  const runIfEditModeIsRow =
    <Args extends any[]>(callback: (...args: Args) => void) =>
    (...args: Args) => {
      if (props.editMode === GridEditModes.Row) {
        callback(...args)
      }
    }

  const throwIfNotEditable = React.useCallback(
    (rowId: ReactTableRowId, field: string) => {
      if (!apiRef.current.isCellEditable({ rowId, field })) {
        throw new Error(
          `The cell with id=${rowId} and field=${field} is not editable.`
        )
      }
    },
    [apiRef]
  )

  const throwIfNotInMode = React.useCallback(
    (id: ReactTableRowId, mode: GridRowModes) => {
      if (apiRef.current.getRowMode(id) !== mode) {
        throw new Error(`The row with id=${id} is not in ${mode} mode.`)
      }
    },
    [apiRef]
  )

  const handleCellDoubleClick = React.useCallback<
    GridEventListener<'cellDoubleClick'>
  >(
    (params, event) => {
      if (!params.isEditable) {
        return
      }
      if (apiRef.current.getRowMode(params.rowId) === GridRowModes.Edit) {
        return
      }

      const newParams: GridRowEditStartParams = {
        row: apiRef.current.tableInstance.getRow(params.rowId),
        rowId: params.rowId,
        field: params.field,
        reason: GridRowEditStartReasons.cellDoubleClick,
      }
      apiRef.current.publishEvent('rowEditStart', newParams, event)
    },
    [apiRef]
  )

  const handleCellFocusIn = React.useCallback<GridEventListener<'cellFocusIn'>>(
    params => {
      nextFocusedCell.current = params
    },
    []
  )

  const handleCellFocusOut = React.useCallback<
    GridEventListener<'cellFocusOut'>
  >(
    (params, event) => {
      if (!params.isEditable) {
        return
      }
      if (apiRef.current.getRowMode(params.rowId) === GridRowModes.View) {
        return
      }
      // The mechanism to detect if we can stop editing a row is different from
      // the cell editing. Instead of triggering it when clicking outside a cell,
      // we must check if another cell in the same row was not clicked. To achieve
      // that, first we keep track of all cells that gained focus. When a cell loses
      // focus we check if the next cell that received focus is from a different row.
      nextFocusedCell.current = null
      focusTimeout.current = setTimeout(() => {
        focusTimeout.current = null
        if (nextFocusedCell.current?.rowId !== params.rowId) {
          // The row might have been deleted during the click
          if (!apiRef.current.tableInstance.getRow(params.rowId)) {
            return
          }

          // The row may already changed its mode
          if (apiRef.current.getRowMode(params.rowId) === GridRowModes.View) {
            return
          }

          const newParams = {
            row: apiRef.current.tableInstance.getRow(params.rowId),
            rowId: params.rowId,
            field: params.field,
            reason: GridRowEditStopReasons.rowFocusOut,
          }
          apiRef.current.publishEvent('rowEditStop', newParams, event)
        }
      })
    },
    [apiRef]
  )

  React.useEffect(() => {
    return () => {
      clearTimeout(focusTimeout.current)
    }
  }, [])

  const handleCellKeyDown = React.useCallback<GridEventListener<'cellKeyDown'>>(
    (params, event) => {
      if (params.cellMode === GridRowModes.Edit) {
        // Wait until IME is settled for Asian languages like Japanese and Chinese
        // TODO: `event.which` is depricated but this is a temporary workaround
        if (event.which === 229) {
          return
        }

        let reason: GridRowEditStopReasons | undefined

        if (event.key === 'Escape') {
          reason = GridRowEditStopReasons.escapeKeyDown
        } else if (event.key === 'Enter') {
          reason = GridRowEditStopReasons.enterKeyDown
        } else if (event.key === 'Tab') {
          const fields = [
            ...apiRef.current.tableInstance.getLeftVisibleLeafColumns(),
            ...apiRef.current.tableInstance.getCenterVisibleLeafColumns(),
            ...apiRef.current.tableInstance.getRightVisibleLeafColumns(),
          ]
          const columnFields = fields
            .filter(one => {
              return apiRef.current.isCellEditable({
                rowId: params.rowId,
                field: one.id,
              })
            })
            .map(one => one.id)

          if (event.shiftKey) {
            if (params.field === columnFields[0]) {
              // Exit if user pressed Shift+Tab on the first field
              reason = GridRowEditStopReasons.shiftTabKeyDown
            }
          } else if (params.field === columnFields[columnFields.length - 1]) {
            // Exit if user pressed Tab on the last field
            reason = GridRowEditStopReasons.tabKeyDown
          }

          // Always prevent going to the next element in the tab sequence because the focus is
          // handled manually to support edit components rendered inside Portals
          event.preventDefault()

          if (!reason) {
            const index = columnFields.findIndex(
              field => field === params.field
            )
            const nextFieldToFocus =
              columnFields[event.shiftKey ? index - 1 : index + 1]

            if (nextFieldToFocus) {
              apiRef.current.setCellFocus(params.rowId, nextFieldToFocus)
            }
          }
        }

        if (reason) {
          const row = apiRef.current.tableInstance.getRow(params.rowId)
          const newParams: GridRowEditStopParams = {
            row: row,
            reason,
            rowId: params.rowId,
            field: params.field,
          }
          apiRef.current.publishEvent('rowEditStop', newParams, event)
        }
      } else if (params.isEditable) {
        let reason: GridRowEditStartReasons | undefined

        if (event.key === ' ' && event.shiftKey) {
          return // Shift + Space is used to select the row
        }

        if (isPrintableKey(event)) {
          reason = GridRowEditStartReasons.printableKeyDown
        } else if ((event.ctrlKey || event.metaKey) && event.key === 'v') {
          reason = GridRowEditStartReasons.printableKeyDown
        } else if (event.key === 'Enter') {
          reason = GridRowEditStartReasons.enterKeyDown
        } else if (event.key === 'Delete' || event.key === 'Backspace') {
          // Delete on Windows, Backspace on macOS
          reason = GridRowEditStartReasons.deleteKeyDown
        }

        if (reason) {
          const row = apiRef.current.tableInstance.getRow(params.rowId)
          const newParams: GridRowEditStartParams = {
            rowId: params.rowId,
            row: row,
            field: params.field,
            key: event.key,
            reason,
          }
          apiRef.current.publishEvent('rowEditStart', newParams, event)
        }
      }
    },
    [apiRef]
  )

  const handleRowEditStart = React.useCallback<
    GridEventListener<'rowEditStart'>
  >(
    params => {
      const { rowId, field, reason, key } = params

      const startRowEditModeParams: GridStartRowEditModeParams = {
        rowId,
        fieldToFocus: field,
      }

      if (reason === GridRowEditStartReasons.printableKeyDown) {
        if (React.version.startsWith('17')) {
          // In React 17, cleaning the input is enough.
          // The sequence of events makes the key pressed by the end-users update the textbox directly.
          startRowEditModeParams.deleteValue = !!field
        } else {
          startRowEditModeParams.initialValue = key
        }
      } else if (reason === GridRowEditStartReasons.deleteKeyDown) {
        startRowEditModeParams.deleteValue = !!field
      }

      apiRef.current.startRowEditMode(startRowEditModeParams)
    },
    [apiRef]
  )

  const handleRowEditStop = React.useCallback<GridEventListener<'rowEditStop'>>(
    params => {
      const { rowId, reason, field } = params

      apiRef.current.runPendingEditCellValueMutation(rowId)

      let cellToFocusAfter: GridStopRowEditModeParams['cellToFocusAfter']
      if (reason === GridRowEditStopReasons.enterKeyDown) {
        cellToFocusAfter = 'below'
      } else if (reason === GridRowEditStopReasons.tabKeyDown) {
        cellToFocusAfter = 'right'
      } else if (reason === GridRowEditStopReasons.shiftTabKeyDown) {
        cellToFocusAfter = 'left'
      }

      const ignoreModifications = reason === 'escapeKeyDown'

      apiRef.current.stopRowEditMode({
        rowId,
        ignoreModifications,
        field,
        cellToFocusAfter,
      })
    },
    [apiRef]
  )

  useGridApiEventHandler(
    apiRef,
    'cellDoubleClick',
    runIfEditModeIsRow(handleCellDoubleClick)
  )
  useGridApiEventHandler(
    apiRef,
    'cellFocusIn',
    runIfEditModeIsRow(handleCellFocusIn)
  )
  useGridApiEventHandler(
    apiRef,
    'cellFocusOut',
    runIfEditModeIsRow(handleCellFocusOut)
  )
  useGridApiEventHandler(
    apiRef,
    'cellKeyDown',
    runIfEditModeIsRow(handleCellKeyDown)
  )

  useGridApiEventHandler(
    apiRef,
    'rowEditStart',
    runIfEditModeIsRow(handleRowEditStart)
  )
  useGridApiEventHandler(
    apiRef,
    'rowEditStop',
    runIfEditModeIsRow(handleRowEditStop)
  )

  useGridApiOptionHandler(apiRef, 'rowEditStart', props.onRowEditStart)
  useGridApiOptionHandler(apiRef, 'rowEditStop', props.onRowEditStop)

  const getRowMode = React.useCallback<GridRowEditingApi['getRowMode']>(
    rowId => {
      if (props.editMode === GridEditModes.Cell) {
        return GridRowModes.View
      }
      const editingState = gridEditRowsStateSelector(apiRef.current.state)
      const isEditing =
        editingState[rowId] && Object.keys(editingState[rowId]!).length > 0
      return isEditing ? GridRowModes.Edit : GridRowModes.View
    },
    [apiRef, props.editMode]
  )

  const updateRowModesModel = useEventCallback(
    (newModel: GridRowModesModel) => {
      const isNewModelDifferentFromProp = newModel !== props.rowModesModel

      if (onRowModesModelChange && isNewModelDifferentFromProp) {
        onRowModesModelChange(newModel)
      }

      if (props.rowModesModel && isNewModelDifferentFromProp) {
        return // The prop always win
      }

      setRowModesModel(newModel)
      rowModesModelRef.current = newModel
      apiRef.current.publishEvent('rowModesModelChange', newModel)
    }
  )

  const updateRowInRowModesModel = React.useCallback(
    (id: ReactTableRowId, newProps: GridRowModesModelProps | null) => {
      const newModel = { ...rowModesModelRef.current }

      if (newProps !== null) {
        newModel[id] = { ...newProps }
      } else {
        delete newModel[id]
      }

      updateRowModesModel(newModel)
    },
    [updateRowModesModel]
  )

  const updateOrDeleteRowState = React.useCallback(
    (id: ReactTableRowId, newProps: GridEditRowProps | null) => {
      apiRef.current.setState(state => {
        const newEditingState: GridEditingState = { ...state.editRows }

        if (newProps !== null) {
          newEditingState[id] = newProps
        } else {
          delete newEditingState[id]
        }

        return { ...state, editRows: newEditingState }
      })
      apiRef.current.forceUpdate()
    },
    [apiRef]
  )

  const updateOrDeleteFieldState = React.useCallback(
    (
      rowId: ReactTableRowId,
      field: string,
      newProps: GridEditCellProps | null
    ) => {
      apiRef.current.setState(state => {
        const newEditingState: GridEditingState = { ...state.editRows }

        if (newProps !== null) {
          newEditingState[rowId] = {
            ...newEditingState[rowId],
            [field]: { ...newProps },
          }
        } else {
          delete newEditingState[rowId]![field]
          if (Object.keys(newEditingState[rowId]!).length === 0) {
            delete newEditingState[rowId]
          }
        }

        return { ...state, editRows: newEditingState }
      })
      apiRef.current.forceUpdate()
    },
    [apiRef]
  )

  const startRowEditMode = React.useCallback<
    GridRowEditingApi['startRowEditMode']
  >(
    params => {
      const { rowId, ...other } = params
      throwIfNotInMode(rowId, GridRowModes.View)
      updateRowInRowModesModel(rowId, { mode: GridRowModes.Edit, ...other })
    },
    [throwIfNotInMode, updateRowInRowModesModel]
  )

  const updateStateToStartRowEditMode = useEventCallback<
    [GridStartRowEditModeParams],
    void
  >(params => {
    const { rowId, fieldToFocus, deleteValue, initialValue } = params

    const columnFields = [
      ...apiRef.current.tableInstance.getLeftVisibleLeafColumns(),
      ...apiRef.current.tableInstance.getCenterVisibleLeafColumns(),
      ...apiRef.current.tableInstance.getRightVisibleLeafColumns(),
    ].map(one => one.id)

    const newProps = columnFields.reduce<Record<string, GridEditCellProps>>(
      (acc, field) => {
        const isEditable = apiRef.current.isCellEditable({ rowId, field })
        if (!isEditable) {
          return acc
        }

        const row = apiRef.current.tableInstance.getRow(rowId)
        let newValue = row.getValue(field)
        if (fieldToFocus === field && (deleteValue || initialValue)) {
          newValue = deleteValue ? '' : initialValue
        }

        acc[field] = {
          value: newValue,
          error: false,
          isProcessingProps: false,
        }
        return acc
      },
      {}
    )
    updateOrDeleteRowState(rowId, newProps)
    if (fieldToFocus) {
      apiRef.current.setCellFocus(rowId, fieldToFocus)
    }
  }) as GridRowEditingApi['stopRowEditMode']

  const stopRowEditMode = React.useCallback<
    GridRowEditingApi['stopRowEditMode']
  >(
    params => {
      const { rowId, ...other } = params
      throwIfNotInMode(rowId, GridRowModes.Edit)
      updateRowInRowModesModel(rowId, { mode: GridRowModes.View, ...other })
    },
    [throwIfNotInMode, updateRowInRowModesModel]
  )

  const updateStateToStopRowEditMode = useEventCallback<
    [GridStopRowEditModeParams],
    void
  >(params => {
    const {
      rowId,
      ignoreModifications,
      field: focusedField,
      cellToFocusAfter = 'none',
    } = params

    apiRef.current.runPendingEditCellValueMutation(rowId)

    const finishRowEditMode = () => {
      if (cellToFocusAfter !== 'none' && focusedField) {
        apiRef.current.moveFocusToRelativeCell(
          rowId,
          focusedField,
          cellToFocusAfter
        )
      }
      updateOrDeleteRowState(rowId, null)
      updateRowInRowModesModel(rowId, null)
    }

    if (ignoreModifications) {
      finishRowEditMode()
      return
    }

    const editingState = gridEditRowsStateSelector(apiRef.current.state)
    const row = apiRef.current.tableInstance.getRow(rowId).original!

    const isSomeFieldProcessingProps = Object.values(editingState[rowId]!).some(
      fieldProps => fieldProps.isProcessingProps
    )

    if (isSomeFieldProcessingProps) {
      prevRowModesModel.current[rowId]!.mode = GridRowModes.Edit
      return
    }

    const hasSomeFieldWithError = Object.values(editingState[rowId]!).some(
      fieldProps => fieldProps.error
    )

    if (hasSomeFieldWithError) {
      prevRowModesModel.current[rowId]!.mode = GridRowModes.Edit
      // Revert the mode in the rowModesModel prop back to "edit"
      updateRowInRowModesModel(rowId, { mode: GridRowModes.Edit })
      return
    }

    const rowUpdate =
      apiRef.current.getRowWithUpdatedValuesFromRowEditing(rowId)

    if (processRowUpdate) {
      const handleError = (errorThrown: any) => {
        prevRowModesModel.current[rowId]!.mode = GridRowModes.Edit
        // Revert the mode in the rowModesModel prop back to "edit"
        updateRowInRowModesModel(rowId, { mode: GridRowModes.Edit })

        if (onProcessRowUpdateError) {
          onProcessRowUpdateError(errorThrown)
        } else {
          missingOnProcessRowUpdateErrorWarning()
        }
      }

      try {
        Promise.resolve(processRowUpdate(rowUpdate, row))
          .then(() => {
            finishRowEditMode()
          })
          .catch(handleError)
      } catch (errorThrown) {
        handleError(errorThrown)
      }
    } else {
      finishRowEditMode()
    }
  }) as GridRowEditingApi['startRowEditMode']

  const setRowEditingEditCellValue = React.useCallback<
    GridRowEditingPrivateApi['setRowEditingEditCellValue']
  >(
    params => {
      const { rowId, field, value, debounceMs } = params

      throwIfNotEditable(rowId, field)

      const column = apiRef.current.tableInstance.getColumn(field)
      const row = apiRef.current.tableInstance.getRow(rowId)!

      const cellKey = `${row.id}_${field}`
      const cell = row.getAllCells().find(one => one.id === cellKey)
      const cellContext = cell?.getContext()

      let editingState = gridEditRowsStateSelector(apiRef.current.state)
      let newProps: GridEditCellProps = {
        ...editingState[rowId]![field],
        value: value,
        changeReason: debounceMs
          ? 'debouncedSetEditCellValue'
          : 'setEditCellValue',
      }

      if (!column?.columnDef?.meta?.preProcessEditCellProps) {
        updateOrDeleteFieldState(rowId, field, newProps)
      }

      return new Promise(resolve => {
        const promises: Promise<void>[] = []

        if (column?.columnDef?.meta?.preProcessEditCellProps) {
          const hasChanged =
            newProps.value !== editingState[rowId]![field]!.value

          newProps = { ...newProps, isProcessingProps: true }
          updateOrDeleteFieldState(rowId, field, newProps)
          /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
          const { [field]: ignoredField, ...otherFieldsProps } =
            editingState[rowId]!

          const promise = Promise.resolve(
            column.columnDef.meta.preProcessEditCellProps({
              rowId,
              field,
              ctx: cellContext!,
              props: newProps,
              hasChanged,
              otherFieldsProps,
            })
          ).then(processedProps => {
            // Check again if the row is in edit mode because the user may have
            // discarded the changes while the props were being processed.
            if (apiRef.current.getRowMode(rowId) === GridRowModes.View) {
              resolve(false)
              return
            }

            editingState = gridEditRowsStateSelector(apiRef.current.state)
            processedProps = { ...processedProps, isProcessingProps: false }
            // We don't reuse the value from the props pre-processing because when the
            // promise resolves it may be already outdated. The only exception to this rule
            // is when there's no pre-processing.
            processedProps.value = column.columnDef!.meta!
              .preProcessEditCellProps
              ? editingState[rowId]![field]!.value
              : value
            updateOrDeleteFieldState(rowId, field, processedProps)
          })

          promises.push(promise)
        }

        Object.entries(editingState[rowId]!).forEach(
          ([thisField, fieldProps]) => {
            if (thisField === field) {
              return
            }

            const fieldColumn =
              apiRef.current.tableInstance.getColumn(thisField)
            if (!fieldColumn?.columnDef?.meta?.preProcessEditCellProps) {
              return
            }

            fieldProps = { ...fieldProps, isProcessingProps: true }
            updateOrDeleteFieldState(rowId, thisField, fieldProps)

            editingState = gridEditRowsStateSelector(apiRef.current.state)
            /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
            const { [thisField]: ignoredField, ...otherFieldsProps } =
              editingState[rowId]!

            const cell = row.getAllCells()?.[thisField as any]
            const cellContext = cell?.getContext()

            const promise = Promise.resolve(
              fieldColumn.columnDef.meta.preProcessEditCellProps({
                rowId,
                field: thisField,
                ctx: cellContext!,
                props: fieldProps,
                hasChanged: false,
                otherFieldsProps,
              })
            ).then(processedProps => {
              // Check again if the row is in edit mode because the user may have
              // discarded the changes while the props were being processed.
              if (apiRef.current.getRowMode(rowId) === GridRowModes.View) {
                resolve(false)
                return
              }

              processedProps = { ...processedProps, isProcessingProps: false }
              updateOrDeleteFieldState(rowId, thisField, processedProps)
            })

            promises.push(promise)
          }
        )

        Promise.all(promises).then(() => {
          if (apiRef.current.getRowMode(rowId) === GridRowModes.Edit) {
            editingState = gridEditRowsStateSelector(apiRef.current.state)
            resolve(!editingState[rowId]![field]!.error)
          } else {
            resolve(false)
          }
        })
      })
    },
    [apiRef, throwIfNotEditable, updateOrDeleteFieldState]
  )

  const getRowWithUpdatedValuesFromRowEditing = React.useCallback<
    GridRowEditingPrivateApi['getRowWithUpdatedValuesFromRowEditing']
  >(
    rowId => {
      const editingState = gridEditRowsStateSelector(apiRef.current.state)
      const row = apiRef.current.tableInstance.getRow(rowId).original

      if (!editingState[rowId]) {
        return apiRef.current.tableInstance.getRow(rowId).original
      }

      let rowUpdate = { ...row }

      Object.entries(editingState[rowId]!).forEach(([field, fieldProps]) => {
        const column = apiRef.current.tableInstance.getColumn(field)
        if (column?.columnDef?.meta?.valueSetter) {
          rowUpdate = column.columnDef.meta.valueSetter({
            value: fieldProps.value,
            row: rowUpdate,
          })
        } else {
          rowUpdate[field] = fieldProps.value
        }
      })

      return rowUpdate
    },
    [apiRef]
  )

  const editingApi: Omit<GridRowEditingApi, keyof GridEditingSharedApi> = {
    getRowMode,
    startRowEditMode,
    stopRowEditMode,
  }

  const editingPrivateApi: Omit<
    GridRowEditingPrivateApi,
    keyof GridEditingSharedPrivateApi
  > = {
    setRowEditingEditCellValue,
    getRowWithUpdatedValuesFromRowEditing,
  }

  useGridApiMethod(apiRef, editingApi, 'public')
  useGridApiMethod(apiRef, editingPrivateApi, 'private')

  React.useEffect(() => {
    if (rowModesModelProp) {
      updateRowModesModel(rowModesModelProp)
    }
  }, [rowModesModelProp, updateRowModesModel])

  React.useEffect(() => {
    // Update the ref here because updateStateToStopRowEditMode may change it later
    const copyOfPrevRowModesModel = prevRowModesModel.current
    prevRowModesModel.current = deepClone(rowModesModel) // Do a deep-clone because the attributes might be changed later

    Object.entries(rowModesModel).forEach(([id, params]) => {
      const prevMode = copyOfPrevRowModesModel[id]?.mode || GridRowModes.View
      const originalId = id
      if (params.mode === GridRowModes.Edit && prevMode === GridRowModes.View) {
        updateStateToStartRowEditMode({ rowId: originalId, ...params })
      } else if (
        params.mode === GridRowModes.View &&
        prevMode === GridRowModes.Edit
      ) {
        updateStateToStopRowEditMode({ rowId: originalId, ...params })
      }
    })
  }, [
    apiRef,
    rowModesModel,
    updateStateToStartRowEditMode,
    updateStateToStopRowEditMode,
  ])
}
